/**
 * Products Redux Selectors
 */

import { createSelector } from '@spcc/core'
import type { ProductsState } from './products.slice'
import {
  applySkuFilters,
  applyVariantFilters,
  sortSkusByName,
  sortVariantsByCode,
  sortCategoriesByName,
} from '../domain/utils'

// ============================================================================
// Root State Type
// ============================================================================

interface RootState {
  products: ProductsState
}

// ============================================================================
// Base Selector
// ============================================================================

const selectProductsState = (state: RootState) => state.products

// ============================================================================
// Simple Selectors
// ============================================================================

export const selectSkus = createSelector(selectProductsState, (state) => state.skus)

export const selectVariants = createSelector(selectProductsState, (state) => state.variants)

export const selectCategories = createSelector(selectProductsState, (state) => state.categories)

export const selectSelectedSkuId = createSelector(
  selectProductsState,
  (state) => state.selectedSkuId
)

export const selectSelectedVariantId = createSelector(
  selectProductsState,
  (state) => state.selectedVariantId
)

export const selectProductsStats = createSelector(selectProductsState, (state) => state.stats)

export const selectProductsFilter = createSelector(selectProductsState, (state) => state.filter)

export const selectProductsLoading = createSelector(selectProductsState, (state) => state.loading)

export const selectProductsSaving = createSelector(selectProductsState, (state) => state.saving)

export const selectPortalAvailabilityUpdatingById = createSelector(
  selectProductsState,
  (state) => state.portalAvailabilityUpdatingById
)

export const selectProductsError = createSelector(selectProductsState, (state) => state.error)

export const selectEditingSku = createSelector(selectProductsState, (state) => state.editingSku)
export const selectEditingVariant = createSelector(selectProductsState, (state) => state.editingVariant)
export const selectEditingCategory = createSelector(selectProductsState, (state) => state.editingCategory)
export const selectEditingLoading = createSelector(selectProductsState, (state) => state.editingLoading)

// ============================================================================
// Computed Selectors - SKUs
// ============================================================================

export const selectSortedSkus = createSelector(selectSkus, (skus) => sortSkusByName(skus))

export const selectFilteredSkus = createSelector(
  [selectSortedSkus, selectProductsFilter],
  (skus, filter) => applySkuFilters(skus, filter)
)

export const selectActiveSkus = createSelector(selectSkus, (skus) =>
  skus.filter((sku) => sku.isActive)
)

export const selectSkuById = (documentId: string) =>
  createSelector(selectSkus, (skus) => skus.find((sku) => sku.documentId === documentId) || null)

export const selectSelectedSku = createSelector(
  [selectSkus, selectSelectedSkuId],
  (skus, selectedId) => (selectedId ? skus.find((sku) => sku.documentId === selectedId) : null)
)

// ============================================================================
// Computed Selectors - Variants
// ============================================================================

export const selectSortedVariants = createSelector(selectVariants, (variants) =>
  sortVariantsByCode(variants)
)

export const selectFilteredVariants = createSelector(
  [selectSortedVariants, selectProductsFilter],
  (variants, filter) => applyVariantFilters(variants, filter)
)

export const selectActiveVariants = createSelector(selectVariants, (variants) =>
  variants.filter((v) => v.isActive)
)

export const selectVariantById = (documentId: string) =>
  createSelector(
    selectVariants,
    (variants) => variants.find((v) => v.documentId === documentId) || null
  )

export const selectVariantsBySkuId = (skuDocumentId: string) =>
  createSelector(selectSortedVariants, (variants) =>
    variants.filter((v) => v.skuDocumentId === skuDocumentId)
  )

export const selectSelectedVariant = createSelector(
  [selectVariants, selectSelectedVariantId],
  (variants, selectedId) =>
    selectedId ? variants.find((v) => v.documentId === selectedId) : null
)

// ============================================================================
// Computed Selectors - Categories
// ============================================================================

export const selectSortedCategories = createSelector(selectCategories, (categories) =>
  sortCategoriesByName(categories)
)

export const selectActiveCategories = createSelector(selectCategories, (categories) =>
  categories.filter((c) => c.isActive)
)

export const selectCategoryById = (documentId: string) =>
  createSelector(
    selectCategories,
    (categories) => categories.find((c) => c.documentId === documentId) || null
  )

// ============================================================================
// Computed Selectors - Combined
// ============================================================================

export const selectSkuWithVariants = (skuDocumentId: string) =>
  createSelector([selectSkuById(skuDocumentId), selectVariantsBySkuId(skuDocumentId)], (sku, variants) => {
    if (!sku) return null
    return { ...sku, variants }
  })

export const selectVariantWithSku = (variantDocumentId: string) =>
  createSelector([selectVariantById(variantDocumentId), selectSkus], (variant, skus) => {
    if (!variant) return null
    const sku = variant.skuDocumentId
      ? skus.find((s) => s.documentId === variant.skuDocumentId)
      : null
    return { ...variant, sku: sku || variant.sku }
  })
